import './bootstrap';

const setupPageScrollbar = () => {
    const scrollbar = document.querySelector('#page-scrollbar');
    const thumb = scrollbar?.querySelector('.page-scrollbar-thumb');

    if (!scrollbar || !thumb || scrollbar.dataset.ready === 'true') {
        return;
    }

    scrollbar.dataset.ready = 'true';

    let frame = null;
    let dragStartY = 0;
    let dragStartScrollTop = 0;

    const getMeasurements = () => {
        const viewportHeight = document.documentElement.clientHeight;
        const contentHeight = document.documentElement.scrollHeight;
        const trackHeight = scrollbar.clientHeight;
        const thumbHeight = Math.min(trackHeight, 24);

        return {
            contentHeight,
            thumbHeight,
            maxScrollTop: Math.max(0, contentHeight - viewportHeight),
            maxThumbTop: Math.max(0, trackHeight - thumbHeight),
        };
    };

    const update = () => {
        frame = null;
        const { contentHeight, thumbHeight, maxScrollTop, maxThumbTop } = getMeasurements();
        const percentage = maxScrollTop ? window.scrollY / maxScrollTop : 0;

        thumb.style.transform = `translateY(${percentage * maxThumbTop}px)`;
        scrollbar.classList.toggle('is-visible', maxScrollTop > 0);
        scrollbar.setAttribute('aria-valuenow', `${Math.round(percentage * 100)}`);
        scrollbar.setAttribute('aria-valuetext', `${Math.round(window.scrollY)} of ${Math.round(contentHeight - document.documentElement.clientHeight)} pixels`);
    };

    const requestUpdate = () => {
        if (frame === null) {
            frame = requestAnimationFrame(update);
        }
    };

    const scrollFromPointer = (clientY, offset) => {
        const { maxScrollTop, maxThumbTop } = getMeasurements();
        const trackRect = scrollbar.getBoundingClientRect();
        const thumbTop = Math.min(maxThumbTop, Math.max(0, clientY - trackRect.top - offset));

        window.scrollTo(0, maxThumbTop ? thumbTop / maxThumbTop * maxScrollTop : 0);
        requestUpdate();
    };

    thumb.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        dragStartY = event.clientY;
        dragStartScrollTop = window.scrollY;
        scrollbar.classList.add('is-dragging');
        thumb.setPointerCapture(event.pointerId);
    });

    thumb.addEventListener('pointermove', (event) => {
        if (!scrollbar.classList.contains('is-dragging')) {
            return;
        }

        const { maxScrollTop, maxThumbTop } = getMeasurements();
        const distance = event.clientY - dragStartY;

        window.scrollTo(0, dragStartScrollTop + (maxThumbTop ? distance / maxThumbTop * maxScrollTop : 0));
        requestUpdate();
    });

    const stopDragging = (event) => {
        scrollbar.classList.remove('is-dragging');
        if (event.pointerId !== undefined && thumb.hasPointerCapture(event.pointerId)) {
            thumb.releasePointerCapture(event.pointerId);
        }
    };

    thumb.addEventListener('pointerup', stopDragging);
    thumb.addEventListener('pointercancel', stopDragging);

    scrollbar.addEventListener('pointerdown', (event) => {
        if (event.target !== scrollbar) {
            return;
        }

        scrollFromPointer(event.clientY, getMeasurements().thumbHeight / 2);
    });

    scrollbar.addEventListener('keydown', (event) => {
        const step = document.documentElement.clientHeight * 0.1;

        if (event.key === 'ArrowDown') window.scrollBy(0, step);
        if (event.key === 'ArrowUp') window.scrollBy(0, -step);
        if (event.key === 'PageDown') window.scrollBy(0, document.documentElement.clientHeight);
        if (event.key === 'PageUp') window.scrollBy(0, -document.documentElement.clientHeight);
        if (event.key === 'Home') window.scrollTo(0, 0);
        if (event.key === 'End') window.scrollTo(0, document.documentElement.scrollHeight);
        requestUpdate();
    });

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    new ResizeObserver(requestUpdate).observe(document.documentElement);
    requestUpdate();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupPageScrollbar, { once: true });
} else {
    setupPageScrollbar();
}

const redirectToLoginIfSessionExpired = (status) => {
    if (status !== 419) {
        return false;
    }

    const loginUrl = document.querySelector('meta[name="login-url"]')?.content;

    if (loginUrl) {
        window.location.assign(loginUrl);
    }

    return true;
};

window.axios.interceptors.response.use(
    (response) => response,
    (error) => {
        redirectToLoginIfSessionExpired(error.response?.status);

        return Promise.reject(error);
    },
);

document.addEventListener('livewire:init', () => {
    Livewire.hook('request', ({ fail }) => {
        fail(({ status, preventDefault }) => {
            if (redirectToLoginIfSessionExpired(status)) {
                preventDefault();
            }
        });
    });
});
